import Swiper from 'swiper';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';

/**
 * LpSlideFade
 * LPのフェードスライダー（Swiper・effect:fade）。KVのコピー切替に使う。
 *
 * 本体サイトの SlideFade.js は本体固有の連携（ドロワー/検索/同期スライダー等）を多く持つため、
 * LPには持ち込まず、必要な機能だけを書き起こしている。
 *
 * **高さを固定するのが要点**（本体サイトで起きた1pxのガタつき対策）：
 *   - スライドごとに文字量が違うため、切り替わるたびに高さが変わって下のコンテンツが動く
 *   - 加えて、高さが小数pxだと切替時の丸め位置が変わって約1pxだけ揺れる
 *   いちばん高いスライドに合わせて整数pxで固定し、どちらも起こらないようにする。
 *   インジケータ側の高さ固定は c-lp-indicator（CSS）で担当。
 *
 * マークアップ：
 *   <div class="p-lp-kv-copy">
 *     <div class="swiper js-lp-slide-fade">
 *       <div class="swiper-wrapper"><div class="swiper-slide">…</div>…</div>
 *     </div>
 *     <div class="c-lp-indicator js-lp-slide-pagination"></div>
 *   </div>
 *
 * data属性で調整：
 *   data-slide-speed  切替にかける時間 ms
 *   data-slide-delay  次へ進むまでの待機 ms
 */

const DEFAULT_SPEED = 1000;
const DEFAULT_DELAY = 6000;
const RESIZE_DELAY = 200;

export default class LpSlideFade {
  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    this.element = element;
    if (!this.element) {
      return;
    }

    this.slides = Array.from(this.element.querySelectorAll('.swiper-slide'));
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.inView = false;
    this.resizeTimer = 0;
    this.lastWidth = null;

    this.onResize = this.onResize.bind(this);
    this.lockHeight = this.lockHeight.bind(this);

    this.initSwiper();
    if (!this.swiper) {
      return;
    }

    this.lockHeight();
    window.addEventListener('load', this.lockHeight);
    window.addEventListener('resize', this.onResize);
    this.observe();
  }

  /**
   * @param {string} name
   * @param {number} fallback
   * @returns {number}
   */
  numberAttr(name, fallback) {
    const value = parseInt(this.element.dataset[name], 10);
    return Number.isNaN(value) ? fallback : value;
  }

  paginationEl() {
    const parent = this.element.parentElement;
    return this.element.querySelector('.js-lp-slide-pagination')
      || (parent && parent.querySelector('.js-lp-slide-pagination'))
      || null;
  }

  initSwiper() {
    const pagination = this.paginationEl();
    const options = {
      modules: [Autoplay, EffectFade, Pagination],
      effect: 'fade',
      fadeEffect: { crossFade: true },
      loop: this.slides.length > 1,
      speed: this.numberAttr('slideSpeed', DEFAULT_SPEED),
      autoplay: this.reduced || this.slides.length <= 1 ? false : {
        delay: this.numberAttr('slideDelay', DEFAULT_DELAY),
        disableOnInteraction: false,
      },
      a11y: {
        paginationBulletMessage: 'スライド {{index}} へ移動',
      },
    };

    if (pagination && this.slides.length > 1) {
      options.pagination = {
        el: pagination,
        clickable: true,
        bulletClass: 'bullet',
        bulletActiveClass: 'is-active',
        renderBullet: (index, className) => `<button type="button" class="${className}" aria-label="スライド ${index + 1} へ移動"></button>`,
      };
    }

    this.swiper = new Swiper(this.element, options);
    this.element.setAttribute('role', 'region');
    if (!this.element.getAttribute('aria-label')) {
      this.element.setAttribute('aria-label', 'キャッチコピー');
    }
  }

  /** いちばん高いスライドに合わせて整数pxで固定する */
  lockHeight() {
    this.element.style.height = '';
    this.lastWidth = Math.round(this.element.getBoundingClientRect().width);

    const tallest = this.slides.reduce((max, slide) => Math.max(max, slide.scrollHeight), 0);
    if (tallest > 0) {
      this.element.style.height = `${Math.ceil(tallest)}px`;
    }
    if (this.swiper && this.swiper.update) {
      this.swiper.update();
    }
  }

  // 高さは自分で書き換えるため、幅が変わった時だけ測り直す（測り直しの無限ループを避ける）
  onResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      const width = Math.round(this.element.getBoundingClientRect().width);
      if (width !== this.lastWidth) {
        this.lockHeight();
      }
    }, RESIZE_DELAY);
  }

  /** 画面外では自動再生を止める（見えていない間にスライドを進めない） */
  observe() {
    if (!this.swiper.autoplay || !('IntersectionObserver' in window)) {
      return;
    }

    // 監視の初回コールバックを待たず、今の位置で判定しておく
    const rect = this.element.getBoundingClientRect();
    this.inView = rect.bottom > 0 && rect.top < window.innerHeight;
    this.applyAutoplay();

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.inView = entry.isIntersecting;
        this.applyAutoplay();
      });
    });
    this.observer.observe(this.element);
  }

  applyAutoplay() {
    const { autoplay } = this.swiper;
    if (!autoplay) {
      return;
    }
    if (this.inView && !autoplay.running) {
      autoplay.start();
    } else if (!this.inView && autoplay.running) {
      autoplay.stop();
    }
  }

  destroy() {
    window.clearTimeout(this.resizeTimer);
    window.removeEventListener('load', this.lockHeight);
    window.removeEventListener('resize', this.onResize);
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.swiper) {
      this.swiper.destroy();
      this.swiper = null;
    }
  }
}
